import { TDataLayer } from "@porsche-customer/porschedatalayer-events/projects/navi/all/types";
import { Component, Element, h, Host, Prop } from "@stencil/core";
import analyticsManager from "@phn/services/analytics/analytics-manager";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import {
  ClickElementType,
  ClickElementTypes,
  ElementTypes,
  EventAction,
  EventActions,
} from "@phn/utility/constants";
import { navContentStore } from "@phn/state/nav-content-store";
import { isModelName, isString } from "@phn/utility/helper";
import { RoutingKeys } from "@phn/entities/routing-key";
import SideDrawerItemText from "./side-drawer-item-text";

@Component({
  tag: "phn-nd-side-drawer-item",
  styleUrl: "side-drawer-item.scss",
  scoped: true,
})
export class SideDrawerItem {
  @Prop() text!: string;

  @Prop() analyticsId!: string;

  @Prop() subtext = "";

  @Prop() elementId = "";

  @Prop() link = "";

  @Prop() iconRight = "";

  @Prop() datalayerEventAction: EventAction = EventActions.NAVIGATION_CLICK;

  @Prop() customDatalayerProperties: TDataLayer = {};

  @Prop() isActive = false;

  @Prop() hasDoubleDrawerAnimationFinished = false;

  @Prop() renderSignatures = false;

  @Element() el!: HTMLPhnNdSideDrawerItemElement;

  private sendDataLayerEvent(
    clickElementType: ClickElementType,
    elementId: string,
  ): void {
    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      this.datalayerEventAction,
      {
        componentClick: {
          clickElementId: this.analyticsId,
          clickElementName: this.text,
          targetElement: analyticsManager.getModelTargetElement(
            elementId,
            this.text,
          ),
          clickElementType,
          ...(clickElementType === ClickElementTypes.Link
            ? { targetUrl: this.link }
            : {}),
        },
        ...this.customDatalayerProperties,
        componentDisplay: {
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementType: analyticsManager.calculateDisplayElementType(
            elementId,
            ElementTypes.sideDrawerItem,
            true,
          ),
          displayElementName: this.text,
        },
        ...(this.datalayerEventAction === EventActions.MODEL_RANGE_CLICK
          ? {
              car: {
                ...(this.customDatalayerProperties.car ?? {}),
                modelRangeName: this.text.toLowerCase(),
              },
            }
          : {}),
      },
    );
  }

  private existsSlot(slotName: string): boolean {
    return this.el.querySelector(`[slot="${slotName}"]`) !== null;
  }

  render(): HTMLPhnNdSideDrawerItemElement {
    const isAccount = this.elementId
      ? this.elementId === RoutingKeys.ACCOUNT
      : this.analyticsId === "mainmenu.account";

    const body = (
      <div class="body-container phn-flex-row phn-align-center">
        {isAccount ? (
          <phn-p-icon
            name="user"
            size="medium"
            role="presentation"
          ></phn-p-icon>
        ) : null}
        <SideDrawerItemText
          isActive={false}
          text={this.text}
          subtext={this.subtext}
          renderSignatures={this.renderSignatures}
          size={isAccount ? "medium" : "inherit"}
        />
        {this.existsSlot("suffix") ? (
          <div>
            <slot name="suffix" />
          </div>
        ) : null}
      </div>
    );

    if (isString(this.link)) {
      const externalShop =
        navContentStore.state.externalShop?.link === this.link;

      return (
        <phn-p-button-pure
          icon={isString(this.iconRight) ? this.iconRight : "none"}
          align-label="start"
          stretch="true"
          size="inherit"
          data-id={this.elementId}
          onClick={() => {
            this.sendDataLayerEvent(ClickElementTypes.Link, this.elementId);
            window.open(this.link, externalShop ? "_blank" : "_self");
          }}
        >
          {body}
        </phn-p-button-pure>
      );
    }

    return (
      <Host
        class={`${this.isActive ? "active" : ""} ${isAccount ? "account" : ""}`}
      >
        <phn-p-button-pure
          active={this.isActive}
          class={isModelName(this.text) ? "models" : ""}
          size="inherit"
          stretch="true"
          data-id={this.elementId}
          onClick={() => {
            this.sendDataLayerEvent(ClickElementTypes.Button, this.elementId);
          }}
          aria={{ "aria-expanded": `${this.isActive ? "true" : "false"}` }}
          icon={"arrow-head-right"}
          align-label="start"
        >
          {body}
        </phn-p-button-pure>
      </Host>
    );
  }
}
